import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { FiEdit2, FiTrash2, FiCheck, FiX, FiChevronRight, FiShare2, FiStar } from "react-icons/fi";
import api from "../api";
import { useAuth } from "../AuthContext";
import LoginPromptModal from "./LoginPromptModal";
import AlbumShareModal from "./AlbumShareModal";
import "./style/LoginPromptModal.css";
import "./style/AlbumModal.css";

const AlbumModal = ({ isOpen, onClose, event, preSelectedImages = [], onAlbumCreated, onAlbumOpen, onNeedSelection }) => {
  const { t } = useTranslation();
  const { authenticated, user } = useAuth();

  const [activeTab, setActiveTab] = useState("new");

  // ── New Album tab ─────────────────────────────────────────────────────────
  const [newAlbumName, setNewAlbumName] = useState("");

  // ── Albums list ───────────────────────────────────────────────────────────
  const [albums, setAlbums] = useState([]);
  const [loadingAlbums, setLoadingAlbums] = useState(false);

  // ── Add to Existing tab ───────────────────────────────────────────────────
  const [targetAlbumId, setTargetAlbumId] = useState(null);

  // ── My Albums tab — list actions ──────────────────────────────────────────
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [sharingAlbum, setSharingAlbum] = useState(null);

  // ── Shared ────────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const hasPreSelection = (preSelectedImages?.length ?? 0) > 0;

  // ── Fetch albums ──────────────────────────────────────────────────────────
  const fetchAlbums = useCallback(async () => {
    if (!event?.event_id) return [];
    setLoadingAlbums(true);
    try {
      const res = await api.get(`/albums?event_id=${event.event_id}`, { withCredentials: true });
      const list = res.data?.albums || [];
      setAlbums(list);
      return list;
    } catch {
      setAlbums([]);
      return [];
    } finally {
      setLoadingAlbums(false);
    }
  }, [event?.event_id]);

  // ── On open ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setNewAlbumName("");
    setTargetAlbumId(null);
    setRenamingId(null);
    setConfirmDeleteId(null);
    setSharingAlbum(null);
    setError(null);
    setActiveTab(hasPreSelection ? "existing" : "new");
    fetchAlbums().then((list) => {
      // Without a pre-selection, land on "My Albums" once the user has at least one album
      if (hasPreSelection) return;
      const ownsEvent = event?.user_id === user?.user_id;
      const hasOwnAlbums = ownsEvent ? list.length > 0 : list.some((a) => a.user_id === user?.user_id);
      if (hasOwnAlbums) setActiveTab((tab) => (tab === "new" ? "manage" : tab));
    });
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;
  if (!authenticated) return <LoginPromptModal isOpen={true} onClose={onClose} source="album" />;

  // ── Create album ──────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setError(null);
    if (!newAlbumName.trim()) { setError(t("album.error_name_required")); return; }

    // No pre-selected images → hand off to gallery selection mode
    if (!hasPreSelection) {
      onNeedSelection && onNeedSelection(newAlbumName.trim());
      onClose();
      return;
    }

    // Pre-selected images → create album immediately
    setSaving(true);
    try {
      await api.post(
        "/albums",
        { event_id: event.event_id, name: newAlbumName.trim(), images: preSelectedImages.map((i) => i.name) },
        { withCredentials: true }
      );
      ReactGA.event({ category: "photographer", action: "album_create_complete", label: preSelectedImages.length.toString() });
      onAlbumCreated && onAlbumCreated();
      onClose();
    } catch {
      setError(t("album.error_generic"));
    } finally {
      setSaving(false);
    }
  };

  // ── Add to existing ───────────────────────────────────────────────────────
  const handleAddToExisting = async () => {
    setError(null);
    if (!targetAlbumId) { setError(t("album.error_select_album")); return; }
    if (!hasPreSelection) { setError(t("album.error_images_required")); return; }
    setSaving(true);
    try {
      await api.post(
        `/albums/${targetAlbumId}/images`,
        { images: preSelectedImages.map((i) => i.name) },
        { withCredentials: true }
      );
      onAlbumCreated && onAlbumCreated();
      onClose();
    } catch {
      setError(t("album.error_generic"));
    } finally {
      setSaving(false);
    }
  };

  // ── Rename ────────────────────────────────────────────────────────────────
  const handleRename = async (albumId) => {
    if (!renameValue.trim()) return;
    try {
      await api.patch(`/albums/${albumId}`, { name: renameValue.trim() }, { withCredentials: true });
      setRenamingId(null);
      fetchAlbums();
    } catch {
      setError(t("album.error_generic"));
    }
  };

  // ── Delete album ──────────────────────────────────────────────────────────
  const handleDelete = async (albumId) => {
    try {
      await api.delete(`/albums/${albumId}`, { withCredentials: true });
      setConfirmDeleteId(null);
      fetchAlbums();
      onAlbumCreated && onAlbumCreated();
    } catch {
      setError(t("album.error_generic"));
    }
  };

  // ── Feature album on guest gallery (event owner only) ────────────────────
  const handleToggleFeatured = async (album) => {
    const featured = !album.is_featured;
    try {
      await api.patch(`/albums/${album.album_id}/featured`, { featured }, { withCredentials: true });
      setAlbums((prev) => prev.map((a) => ({
        ...a,
        is_featured: a.album_id === album.album_id ? featured : featured ? false : a.is_featured,
      })));
    } catch {
      setError(t("album.error_generic"));
    }
  };

  // ── Open album in gallery ─────────────────────────────────────────────────
  const handleOpenAlbum = (album) => {
    onClose();
    onAlbumOpen && onAlbumOpen(album);
  };

  // ── My albums filter ──────────────────────────────────────────────────────
  const isEventOwner = event?.user_id === user?.user_id;
  const visibleAlbums = isEventOwner ? albums : albums.filter((a) => a.user_id === user?.user_id);

  // ── Render: New Album tab ─────────────────────────────────────────────────
  const renderNew = () => (
    <>
      <input
        className="am-input"
        type="text"
        placeholder={t("album.name_placeholder")}
        value={newAlbumName}
        onChange={(e) => setNewAlbumName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleCreate()}
        autoFocus
      />
      {hasPreSelection ? (
        <p className="am-count-label">{t("album.create_with_count", { count: preSelectedImages.length })}</p>
      ) : (
        <p className="am-instruction">{t("album.select_after_create")}</p>
      )}
    </>
  );

  // ── Render: Add to Existing tab ───────────────────────────────────────────
  const renderExisting = () => {
    if (loadingAlbums) return <div className="am-loading"><div className="spinner-border spinner-border-sm text-secondary" /></div>;
    if (albums.length === 0) return <p className="am-empty">{t("album.no_albums")}</p>;
    return (
      <>
        <p className="am-count-label">{t("album.selected_count", { count: preSelectedImages.length })}</p>
        <div className="am-album-list">
          {albums.map((album) => (
            <div
              key={album.album_id}
              className={`am-album-row${targetAlbumId === album.album_id ? " am-album-row--selected" : ""}`}
              onClick={() => setTargetAlbumId(album.album_id)}
            >
              <div className="am-album-meta">
                <span className="am-album-name">
                  {album.name}
                  {isEventOwner && album.is_featured && (
                    <span className="am-featured-pill">{t("album.featured_badge")}</span>
                  )}
                </span>
                {isEventOwner && album.owner_name && (
                  <span className="am-album-owner">{album.owner_name}</span>
                )}
              </div>
              <span className="am-album-count">{t("album.photo_count", { count: album.photo_count })}</span>
            </div>
          ))}
        </div>
      </>
    );
  };

  // ── Render: My Albums list ────────────────────────────────────────────────
  const renderManage = () => {
    if (loadingAlbums) return <div className="am-loading"><div className="spinner-border spinner-border-sm text-secondary" /></div>;
    if (visibleAlbums.length === 0) return <p className="am-empty">{t("album.no_own_albums")}</p>;

    return (
      <div className="am-album-list">
        {visibleAlbums.map((album) => {
          if (confirmDeleteId === album.album_id) {
            return (
              <div key={album.album_id} className="am-album-row am-album-row--manage">
                <div className="am-confirm-row">
                  <span className="am-confirm-label">{t("album.confirm_delete")}</span>
                  <button className="am-icon-btn am-icon-btn--danger" onClick={() => handleDelete(album.album_id)}>
                    <FiTrash2 />
                  </button>
                  <button className="am-icon-btn" onClick={() => setConfirmDeleteId(null)}>
                    <FiX />
                  </button>
                </div>
              </div>
            );
          }

          if (renamingId === album.album_id) {
            return (
              <div key={album.album_id} className="am-album-row am-album-row--manage">
                <input
                  className="am-rename-input"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleRename(album.album_id); if (e.key === "Escape") setRenamingId(null); }}
                  autoFocus
                />
                <button className="am-icon-btn am-icon-btn--confirm" onClick={() => handleRename(album.album_id)}>
                  <FiCheck />
                </button>
                <button className="am-icon-btn" onClick={() => setRenamingId(null)}>
                  <FiX />
                </button>
              </div>
            );
          }

          return (
            <div
              key={album.album_id}
              className="am-album-row am-album-row--manage am-album-row--clickable"
              onClick={() => handleOpenAlbum(album)}
            >
              <div className="am-album-meta">
                <span className="am-album-name">
                  {album.name}
                  {isEventOwner && album.is_featured && (
                    <span className="am-featured-pill">{t("album.featured_badge")}</span>
                  )}
                </span>
                {isEventOwner && album.owner_name && (
                  <span className="am-album-owner">{album.owner_name}</span>
                )}
              </div>
              <span className="am-album-count">{t("album.photo_count", { count: album.photo_count })}</span>
              {isEventOwner && (
                <button
                  className={`am-icon-btn am-feature-btn${album.is_featured ? " am-feature-btn--active" : ""}`}
                  onClick={(e) => { e.stopPropagation(); handleToggleFeatured(album); }}
                  title={t(album.is_featured ? "album.feature_toggle_off" : "album.feature_toggle_on")}
                  aria-label={t(album.is_featured ? "album.feature_toggle_off" : "album.feature_toggle_on")}
                  aria-pressed={!!album.is_featured}
                >
                  <FiStar fill={album.is_featured ? "currentColor" : "none"} />
                </button>
              )}
              <button
                className="am-icon-btn"
                onClick={(e) => { e.stopPropagation(); setSharingAlbum(album); setRenamingId(null); setConfirmDeleteId(null); }}
                title={t("album_share.open")}
                aria-label={t("album_share.open")}
              >
                <FiShare2 />
              </button>
              <button
                className="am-icon-btn"
                onClick={(e) => { e.stopPropagation(); setRenamingId(album.album_id); setRenameValue(album.name); setConfirmDeleteId(null); }}
                title="Rename"
              >
                <FiEdit2 />
              </button>
              <button
                className="am-icon-btn am-icon-btn--danger"
                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(album.album_id); setRenamingId(null); }}
                title="Delete"
              >
                <FiTrash2 />
              </button>
              <FiChevronRight style={{ color: "#d1d5db", flexShrink: 0 }} />
            </div>
          );
        })}
      </div>
    );
  };

  // ── Footer ────────────────────────────────────────────────────────────────
  const renderFooter = () => {
    if (activeTab === "new") {
      return (
        <div className="am-footer">
          <button className="lpm-btn lpm-btn--secondary" onClick={onClose} disabled={saving}>{t("album.cancel_btn")}</button>
          <button className="lpm-btn lpm-btn--primary" onClick={handleCreate} disabled={saving}>
            {saving ? t("album.saving") : t("album.create_btn")}
          </button>
        </div>
      );
    }
    if (activeTab === "existing") {
      return (
        <div className="am-footer">
          <button className="lpm-btn lpm-btn--secondary" onClick={onClose} disabled={saving}>{t("album.cancel_btn")}</button>
          <button
            className="lpm-btn lpm-btn--primary"
            onClick={handleAddToExisting}
            disabled={saving || !targetAlbumId || !hasPreSelection}
          >
            {saving ? t("album.saving") : t("album.add_btn", { count: preSelectedImages.length })}
          </button>
        </div>
      );
    }
    return (
      <div className="am-footer">
        <button className="lpm-btn lpm-btn--secondary" onClick={onClose} style={{ flex: "none", minWidth: 80 }}>
          {t("album.cancel_btn")}
        </button>
      </div>
    );
  };

  if (sharingAlbum) return <AlbumShareModal album={sharingAlbum} onClose={() => setSharingAlbum(null)} />;

  return (
    <div className="lpm-overlay" onClick={onClose}>
      <div className="am-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="am-header">
          <h2 className="am-title">{t("album.modal_title")}</h2>
          <button className="am-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Tabs */}
        <div className="am-tabs">
          <button
            className={`am-tab${activeTab === "new" ? " am-tab--active" : ""}`}
            onClick={() => { setActiveTab("new"); setError(null); }}
          >
            {t("album.tab_new")}
          </button>
          {hasPreSelection && (
            <button
              className={`am-tab${activeTab === "existing" ? " am-tab--active" : ""}`}
              onClick={() => { setActiveTab("existing"); setError(null); }}
            >
              {t("album.tab_existing")}
            </button>
          )}
          <button
            className={`am-tab${activeTab === "manage" ? " am-tab--active" : ""}`}
            onClick={() => { setActiveTab("manage"); setError(null); }}
          >
            {t("album.tab_manage")}
          </button>
        </div>

        {/* Body */}
        <div className="am-body">
          {error && <p className="am-error">{error}</p>}
          {activeTab === "new" && renderNew()}
          {activeTab === "existing" && renderExisting()}
          {activeTab === "manage" && renderManage()}
        </div>

        {renderFooter()}
      </div>
    </div>
  );
};

export default AlbumModal;
